/**
 * «Опишите, что нужно, — ИИ заполнит поля» — the AI block on top of the
 * saved-search builder.
 *
 * Different job from the onboarding step, and deliberately a different shape.
 * In onboarding the proposal IS the screen; here the builder is the screen and
 * the model is a way to fill it in. So there is no review step, no second
 * screen and no separate save path: the answer lands in the fields the user
 * already knows, every one of them still editable, and «Сохранить» is the same
 * button it was before.
 *
 * Which is also why the failure states are quieter here. The manual form is
 * already on screen underneath — there is nothing to offer as an alternative
 * and nothing to rescue the user from.
 */
import { useEffect, useState } from 'react';
import { useLocaleNavigate } from '../i18n/locale-link';
import { useLocale } from '../i18n/locale-context';
import { AI_MAX_TEXT, type AiProposeResponse } from '../lib/ai-search-client';
import { useAiPropose } from '../lib/use-ai-propose';
import { AiQuotaLine, AiQuotaOutCard, AiSparkle, aiPrimaryClass } from './ai-search-cards';
import { canAsk } from './ai-onboarding-step';

export function AiFillBlock({ onFilled }: { onFilled: (res: AiProposeResponse) => void }) {
  const { t, locale } = useLocale();
  const navigate = useLocaleNavigate();
  const [text, setText] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const ai = useAiPropose('saved_search', locale, true);
  const [handled, setHandled] = useState<string | null>(null);

  /**
   * Hand the answer up to the builder, once per proposal.
   *
   * In an effect, not during render: `onFilled` writes the PARENT's state, and
   * React refuses to update one component while another is rendering (it is a
   * warning today and a torn render tomorrow). The effect still runs before
   * paint, so there is no frame where this block says «заполнено» over empty
   * fields. `handled` is the idempotency guard — `ai.result` keeps its identity
   * across the parent re-render this very call causes.
   */
  useEffect(() => {
    if (ai.phase !== 'ready' || !ai.result) return;
    if (handled === ai.result.draftId) return;
    setHandled(ai.result.draftId);
    onFilled(ai.result);
  }, [ai.phase, ai.result, handled, onFilled]);

  if (dismissed) return null;

  if (ai.phase === 'failed' && ai.failure === 'ai_quota_exhausted') {
    return (
      <div className="mb-1">
        <AiQuotaOutCard
          quota={ai.quota}
          // «Собрать вручную» is literally the form below — so the only honest
          // thing this button can do is get out of its way.
          onManual={() => setDismissed(true)}
          onPro={() => navigate('/plans?src=ai_saved_search')}
        />
      </div>
    );
  }

  const busy = ai.phase === 'loading';
  const ready = canAsk(text);
  const failed = ai.phase === 'failed';

  return (
    <div className="rounded-xl border border-brand/25 bg-brand-dim p-3">
      <p className="mb-2 flex items-center gap-2 text-[13.5px] font-bold text-ink">
        <AiSparkle />
        {t('aiOnboarding.fill.title')}
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, AI_MAX_TEXT))}
        rows={2}
        maxLength={AI_MAX_TEXT}
        disabled={busy}
        aria-label={t('aiOnboarding.fill.title')}
        placeholder={t('aiOnboarding.fill.placeholder')}
        className="w-full resize-none rounded-lg border border-brand/40 bg-bone-surface px-3 py-2 text-[16px] leading-snug text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand disabled:opacity-60"
      />
      <button
        type="button"
        onClick={() => ai.ask({ text })}
        disabled={!ready || busy}
        className={`${aiPrimaryClass} mt-2 h-11 w-full`}
      >
        {busy ? t('aiOnboarding.step1.loading') : t('aiOnboarding.fill.submit')}
      </button>
      {failed && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] leading-snug text-ink-soft">
          <span>
            {ai.failure === 'ai_not_understood'
              ? t('aiOnboarding.notUnderstood.desc')
              : t('aiOnboarding.error.desc')}
          </span>
          {/* Only for the outages. «Не понял» is answered by editing the text,
              and a retry button next to an unchanged sentence just spends time. */}
          {ai.failure !== 'ai_not_understood' && (
            <button
              type="button"
              onClick={ai.retry}
              className="font-semibold text-brand underline underline-offset-2"
            >
              {t('aiOnboarding.error.retry')}
            </button>
          )}
        </p>
      )}
      <AiQuotaLine quota={ai.quota} variant="sentence" className="mt-2" />
    </div>
  );
}
